import { useState, useEffect, useRef } from 'react';
import { Search, X, FileText, Clock, Trash2, Bookmark } from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { useDocSearch } from '@/hooks/useDocSearch';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import { useProject } from '@/contexts/ProjectContext';
import { DocTypeBadge } from '@/components/docs/DocTypeBadge';
import { BookmarkToggle } from '@/components/docs/BookmarkToggle';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { docDeepLinkPath, withDocParam } from '@/lib/docDeepLink';
import {
  isLaunchHost,
  getTenantSlug,
  rememberedTenantSlug,
  launchUrl,
  captureAppUrl,
} from '@/utils/subdomain';

interface SearchBarProps {
  /** Render the closed trigger as a full-width search bar (for the header
   *  centre) instead of the compact icon button. */
  expanded?: boolean;
  /** Render the closed trigger as a bare magnifier icon button (for the mobile
   *  header, next to the notification bell). Takes precedence over `expanded`. */
  iconOnly?: boolean;
  className?: string;
}

// Platform-aware shortcut hint. Starlight's search button on the docs portal
// already renders ⌘K on macOS and Ctrl K elsewhere, so matching it here keeps
// the badge identical when hopping between surfaces.
const IS_MAC =
  typeof navigator !== 'undefined' &&
  /Mac|iP(hone|ad|od)/.test(navigator.platform || navigator.userAgent);
const SHORTCUT_HINT = IS_MAC ? '⌘K' : 'Ctrl K';

export function SearchBar({ expanded = false, iconOnly = false, className }: SearchBarProps = {}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [repoFilter, setRepoFilter] = useState<string>();
  const [typeFilter, setTypeFilter] = useState<string>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { results, total, loading, search } = useDocSearch();
  const { recentSearches, record: recordSearch, dismiss: dismissSearch, clearAll: clearAllSearches } = useRecentSearches();
  // Selected project scopes results server-side (view filter — the backend
  // still enforces spaces first).
  const { selected: selectedProject } = useProject();
  const selectedProjectId = selectedProject?.projectId;
  const lastRecordedQuery = useRef<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { search: currentSearch } = useLocation();

  // Cmd+K shortcut
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    search(query, { repo: repoFilter, type: typeFilter, project: selectedProjectId });
    setSelectedIndex(0);
  }, [query, repoFilter, typeFilter, selectedProjectId, search]);

  // Record search after results arrive (fire-and-forget, only when query has results)
  useEffect(() => {
    if (query.trim() && total > 0 && !loading && query !== lastRecordedQuery.current) {
      lastRecordedQuery.current = query;
      recordSearch(query, total, { repo: repoFilter, type: typeFilter, project: selectedProjectId });
    }
  }, [query, total, loading, repoFilter, typeFilter, selectedProjectId, recordSearch]);

  function handleSelect(docId: string) {
    setOpen(false);
    setQuery('');
    setRepoFilter(undefined);
    setTypeFilter(undefined);
    // The doc viewer lives on the launch home ({slug}.systemdox.com), where
    // DocViewerPage opens whatever `?doc=` names. On any other host — the
    // Build console, the capture app — hop across instead of dead-ending on
    // that host's redirects; launchUrl carries the query string over. Without a
    // resolvable slug there is no launch host to target, so fall back to the
    // neutral capture app.
    if (!isLaunchHost()) {
      const slug = getTenantSlug() ?? rememberedTenantSlug();
      window.location.href = slug
        ? launchUrl(slug, docDeepLinkPath(docId))
        : captureAppUrl(docDeepLinkPath(docId), null);
      return;
    }
    navigate({ pathname: '/docs', search: withDocParam(currentSearch, docId) });
  }

  function handleRecentSearchClick(entry: typeof recentSearches[0]) {
    setQuery(entry.query);
    setRepoFilter(entry.filters?.repo);
    setTypeFilter(entry.filters?.type);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex].id);
    }
  }

  function highlightMatch(text: string, q: string) {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        {/* <mark> takes its colour from the UA (MarkText), which does not
            follow our theme — so the match must state its own, or it comes out
            near-black on the dark highlight. */}
        <mark className="bg-yellow-200 text-slate-900 dark:bg-yellow-800/50 dark:text-yellow-50 rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  }

  // Collect unique repos and types from results for filter chips
  const repos = [...new Set(results.map((r) => r.repo_full_name))];
  const types = [...new Set(results.map((r) => r.doc_type))];

  if (!open) {
    if (iconOnly) {
      return (
        <button
          onClick={() => setOpen(true)}
          aria-label="Search documents"
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors',
            className,
          )}
        >
          <Search className="w-5 h-5" />
        </button>
      );
    }
    if (expanded) {
      return (
        <button
          onClick={() => setOpen(true)}
          className={cn(
            'flex w-full items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-sm text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 transition-colors',
            className,
          )}
        >
          <Search className="w-4 h-4 shrink-0" />
          <span className="flex-1 text-left">Search documents...</span>
          <kbd className="hidden sm:inline text-[10px] px-1.5 py-0.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 font-mono">
            {SHORTCUT_HINT}
          </kbd>
        </button>
      );
    }
    return (
      <button
        onClick={() => setOpen(true)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-400 hover:border-slate-300 dark:hover:border-slate-600 transition-colors',
          className,
        )}
      >
        <Search className="w-3.5 h-3.5" />
        <span>Search docs...</span>
        <kbd className="hidden sm:inline text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono">
          {SHORTCUT_HINT}
        </kbd>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]" onClick={() => setOpen(false)}>
      <div className="fixed inset-0 bg-black/50" />
      <div
        className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search documents..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-sm text-slate-900 dark:text-slate-100 outline-none placeholder:text-slate-400"
          />
          {loading && <Spinner size="sm" className="shrink-0" />}
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Filter chips */}
        {query && (repos.length > 1 || types.length > 1) && (
          <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
            {repos.length > 1 && repos.map((r) => (
              <button
                key={r}
                onClick={() => setRepoFilter(repoFilter === r ? undefined : r)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 transition-colors ${
                  repoFilter === r
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700'
                }`}
              >
                {r.split('/')[1]}
              </button>
            ))}
            {types.length > 1 && types.map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(typeFilter === t ? undefined : t)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 transition-colors ${
                  typeFilter === t
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        <div className="max-h-80 overflow-y-auto">
          {results.length === 0 && query && !loading && (
            <p className="px-4 py-8 text-center text-sm text-slate-500">No results found</p>
          )}
          {results.map((doc, i) => (
            <div
              key={doc.id}
              onClick={() => handleSelect(doc.id)}
              role="button"
              tabIndex={0}
              className={`flex items-start gap-3 w-full px-4 py-2.5 text-left transition-colors cursor-pointer ${
                i === selectedIndex ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
              }`}
            >
              <FileText className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                  {highlightMatch(doc.title, query)}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{doc.repo_full_name}/{doc.path}</p>
                {doc.snippet && (
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 line-clamp-2">
                    {highlightMatch(doc.snippet, query)}
                  </p>
                )}
              </div>
              <DocTypeBadge type={doc.doc_type} />
              <BookmarkToggle
                docId={doc.id}
                title={doc.title}
                repoFullName={doc.repo_full_name}
                path={doc.path}
                docType={doc.doc_type}
              />
            </div>
          ))}
        </div>

        {query && total > results.length && (
          <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-800 text-xs text-slate-400 text-center">
            Showing {results.length} of {total} results
          </div>
        )}

        {!query && recentSearches.length > 0 && (
          <div className="max-h-80 overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-800">
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Recent searches</span>
              <button
                onClick={clearAllSearches}
                className="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                Clear all
              </button>
            </div>
            {recentSearches.map((entry) => (
              <div key={entry.searchId} className="flex items-center gap-3 w-full px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group">
                <button
                  onClick={() => handleRecentSearchClick(entry)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                >
                  <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="text-sm text-slate-700 dark:text-slate-300 truncate">{entry.query}</span>
                  <span className="text-[10px] text-slate-400 shrink-0">{entry.resultCount} results</span>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); dismissSearch(entry.searchId); }}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-opacity shrink-0"
                  aria-label="Remove"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {!query && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 dark:border-slate-800">
            <span className="text-xs text-slate-400">
              {recentSearches.length === 0 ? 'Type to search across all indexed documents' : ''}
            </span>
            <button
              onClick={() => { setOpen(false); navigate('/bookmarks'); }}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors"
            >
              <Bookmark className="w-3.5 h-3.5" />
              Bookmarks
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
